import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarModule } from '@angular/material/snack-bar';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatStepperModule } from '@angular/material/stepper';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { ProjectService } from '../../../../../../common/services/project.service';
import { CreateRefund, ProjectType, Refund, UpdateRefund } from '../../../../../../common/models/project';
import { Subject, takeUntil } from 'rxjs';
import { MatDatepickerModule } from '@angular/material/datepicker';

@Component({
  selector: 'app-refund-detail',
  templateUrl: './refund.component.html',
  styleUrls: ['./refund.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatCardModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatCheckboxModule,
    MatIconModule,
    MatSelectModule,
    MatSnackBarModule,
    MatDatepickerModule
]
})
export class RefundComponent implements OnInit {
  private destroy$ = new Subject<void>();
  isHandset: boolean = false;

  refundForm: FormGroup;
  isUpdating: boolean = false;
  projectId!: string;
  refundId!: string;

  public ProjectType = ProjectType;

  private readonly snackbarConfig: MatSnackBarConfig = {
    duration: 3000,
    horizontalPosition: 'right',
    verticalPosition: 'top'
  };

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private projectService: ProjectService,
    private snackBar: MatSnackBar,
    private router: Router,
    private breakpointObserver: BreakpointObserver
  ) {

    this.refundForm = this.fb.group({
      classificationCode: [null, Validators.required],
      referenceNumber: [null, Validators.required],
      date: [null],
    });
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(takeUntil(this.destroy$)).subscribe((result) => {
        this.isHandset = result.matches;
      });

    this.projectId = this.route.snapshot.paramMap.get('projectId') || '';
    const refundId = this.route.snapshot.paramMap.get('refundId');

    if (refundId) {
      this.isUpdating = true;
      this.refundId = refundId;
      this.loadRefund(this.projectId, this.refundId);
    } else {
      this.isUpdating = false;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadRefund(projectId: string, contractId: string) {
    this.projectService.getRefund(projectId, contractId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (refund: Refund) => {
        this.refundForm.patchValue({
          classificationCode: refund.classificationCode,
          referenceNumber: refund.referenceNumber,
          date: refund.date
        });
      },
      error: (err) => {
        console.error('Error loading refund', err);
        this.snackBar.open(
          'Dogodila se greška prilikom učitavanja Odluke o povratu.', 'Zatvori', this.snackbarConfig
        );
      }
    });
  }

  onSubmit() {
    if (this.refundForm.invalid) {
      this.snackBar.open('Molimo ispunite sva obavezna polja prije spremanja.', 'Zatvori', this.snackbarConfig );
      return;
    }

    if (this.isUpdating) {
      this.updateRefund();
    } else {
      this.createRefund();
    }
  }

  private updateRefund() {
    const dto: UpdateRefund = {
      ...this.refundForm.value
    };

    this.projectService
    .updateRefund(this.projectId, this.refundId, dto).pipe(takeUntil(this.destroy$)).subscribe({
        next: () => {
          this.snackBar.open('Uspješno ste ažurirali Odluku o povratu.', 'Zatvori', this.snackbarConfig );
          this.router.navigate(['municipality', 'projects', this.projectId, 'refunds']);
        },
        error: (error) => {
          console.error('Error updating refund', error);
          this.snackBar.open('Greška prilikom ažuriranja Odluke o povratu', 'Zatvori',  this.snackbarConfig);
        }
      });
  }

  private createRefund() {
    const dto: CreateRefund = {
      ...this.refundForm.value
    };

    this.projectService.createRefund(this.projectId, dto).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.snackBar.open('Uspješno ste dodali novu Odluku o povratu.', 'Zatvori', this.snackbarConfig);
        this.router.navigate(['municipality', 'projects', this.projectId, 'refunds']);
      },
      error: (error) => {
        console.error('Error creating refund', error);
        this.snackBar.open('Greška prilikom kreiranja Odluke o povratu', 'Zatvori', this.snackbarConfig);
      }
    });
  }
}
